import '../styles/RunMonitorLogTicker.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

/* Seeded entries for the monitored run: deterministic example log lines (no simulated execution). */
const SEED_LOGS = {
  'run-2026-02-07-03': [
    { time: '11:18:02', level: 'info', msg: 'Run accepted · target partner-api.example' },
    { time: '11:18:03', level: 'info', msg: 'Policy loaded · Wordlist · 4 threads · default policy' },
    { time: '11:18:07', level: 'info', msg: 'Wordlist parsed · candidates queued' },
    { time: '11:18:12', level: 'info', msg: 'Worker 1..4 online · resuming from checkpoint' },
    { time: '11:18:19', level: 'warn', msg: 'Attempt threshold reached for user "api-svc": weak candidate flagged' },
    { time: '11:18:26', level: 'info', msg: 'Append-only log · local store · no cloud sync' },
    { time: '11:19:44', level: 'warn', msg: 'Weak candidate flagged · user "api-svc" · queued for report' },
    { time: '11:21:10', level: 'info', msg: 'Progress saved · history record run-2026-02-07-03 updated' },
  ],
  'run-2026-02-09-01': [
    { time: '17:05:01', level: 'info', msg: 'Run accepted · target legacy-admin.corp' },
    { time: '17:05:02', level: 'info', msg: 'Policy loaded · Mask · 8 threads · default policy' },
    { time: '17:06:31', level: 'warn', msg: 'Worker 5 dropped its connection to the target' },
    { time: '17:07:12', level: 'err', msg: 'Mask step aborted · failing step: mask expansion' },
    { time: '17:12:00', level: 'err', msg: 'Run stopped · state failed · retry available' },
  ],
  'run-2026-02-10-02': [
    { time: '09:41:03', level: 'info', msg: 'Run accepted · target vpn-gw-01.corp' },
    { time: '09:41:04', level: 'info', msg: 'Policy loaded · Wordlist · 2 threads · strict policy' },
    { time: '09:43:26', level: 'ok', msg: 'Weak candidate confirmed · report row written' },
    { time: '09:58:00', level: 'ok', msg: 'Run complete · log closed' },
  ],
  'run-2026-02-11-01': [
    { time: '14:22:01', level: 'info', msg: 'Run accepted · target staging-auth.internal' },
    { time: '14:22:03', level: 'info', msg: 'Policy loaded · Wordlist · 4 threads · default policy' },
    { time: '14:24:51', level: 'warn', msg: 'Attempt threshold reached for user "svc-staging"' },
    { time: '14:31:17', level: 'ok', msg: 'Weak candidate confirmed · report row written' },
    { time: '14:39:00', level: 'ok', msg: 'Run complete · log closed' },
  ],
};

const DEFAULT_LOGS = [
  { time: '11:18:02', level: 'info', msg: 'Run accepted' },
  { time: '11:18:03', level: 'info', msg: 'Policy loaded' },
  { time: '11:18:07', level: 'info', msg: 'Wordlist parsed · candidates queued' },
  { time: '11:18:12', level: 'info', msg: 'Workers online · resuming from checkpoint' },
  { time: '11:18:26', level: 'info', msg: 'Append-only log · local store · no cloud sync' },
];

function selectRunId() {
  if (typeof window === 'undefined') return null;
  const id = new URLSearchParams(window.location.search).get('id');
  return id || null;
}

function toLogLines(seed) {
  return seed.map((line) => ({ ...line }));
}

function LogLine({ line }) {
  return (
    <span className="rmlt-line">
      <span className="rmlt-line__time">{line.time}</span>
      <span className={`rmlt-line__level rmlt-line__level--${line.level}`}>{line.level === 'err' ? 'fail' : line.level}</span>
      <span className="rmlt-line__msg">{line.msg}</span>
    </span>
  );
}

export default function RunMonitorLogTicker() {
  const { data, loading, error } = useProjectCollection('test_run');
  const runs = Array.isArray(data) ? data : [];
  const [requestedId] = useState(() => selectRunId());
  const [streamStarted, setStreamStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);

  const run = useMemo(() => {
    if (!runs.length) return null;
    if (requestedId) {
      const match = runs.find((r) => r.id === requestedId);
      if (match) return match;
    }
    const active = runs.filter((r) => r.state === 'running').sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
    return active[0] || runs.slice().sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0];
  }, [runs, requestedId]);

  const [logLines, setLogLines] = useState(DEFAULT_LOGS);

  useEffect(() => {
    if (run && SEED_LOGS[run.id]) {
      setLogLines(toLogLines(SEED_LOGS[run.id]));
    }
  }, [run]);

  /* Minimal entrance so streamed lines arrive with a wipe, not a fade. */
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver !== 'function') {
      setStreamStarted(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setStreamStarted(true);
        observer.disconnect();
      }
    }, { threshold: 0.2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const isLive = Boolean(run) && run.state === 'running';
  const marqueePaused = paused || !streamStarted;

  return (
    <section
      ref={sectionRef}
      className={`rmlt-section${paused ? ' rmlt-section--paused' : ''}${paused ? ' rmlt-section--user-paused' : ''}`}
      aria-label="Live run log"
      data-preview-scroll="1"
    >
      <div className="rmlt-bar">
        <div className="rmlt-live">
          <span className={`rmlt-live__dot${isLive ? ' rmlt-live__dot--pulse' : ''}`} aria-hidden="true" />
          <span className="rmlt-live__label">{isLive ? 'Live' : 'Log'}</span>
          <span className="rmlt-live__run">
            {loading && !run ? 'Loading run…' : error ? 'Run unavailable' : run ? `${run.id}` : 'No run selected'}
          </span>
        </div>

        <div className="rmlt-band">
          <div
            className="rmlt-marquee"
            role="group"
            aria-label="Streaming log lines. Focus the pause control to hold them."
          >
            <div
              className="rmlt-marquee__track"
              style={marqueePaused ? { animationPlayState: 'paused' } : undefined}
            >
              <div className="rmlt-marquee__group">
                {logLines.map((line, i) => <LogLine key={`a-${line.time}-${i}`} line={line} />)}
              </div>
              <div className="rmlt-marquee__group" aria-hidden="true">
                {logLines.map((line, i) => <LogLine key={`b-${line.time}-${i}`} line={line} />)}
              </div>
            </div>
          </div>

          <ul className="rmlt-static" aria-label={run ? `Full log for ${run.name}` : 'Full log'}>
            {logLines.map((line, i) => (
              <li className="rmlt-static__row" key={`s-${line.time}-${i}`}>
                <LogLine line={line} />
              </li>
            ))}
          </ul>

          <button
            type="button"
            className="rmlt-pause"
            aria-pressed={paused}
            onClick={() => setPaused((v) => !v)}
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>
    </section>
  );
}